import {
    Component,
    Input,
    ElementRef,
    OnInit,
    Output,
    EventEmitter,
    ViewChild,
    ChangeDetectorRef
} from '@angular/core';

declare var daum: any;
const url = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

@Component({
  selector: 'btn-daum-address',
  templateUrl: './daum-address.component.html',
  styleUrls: ['./daum-address.component.scss']
})
export class DaumAddressComponent implements OnInit {
  @Output() result = new EventEmitter<Object>();
  @Input() options: any;
  @ViewChild('postcodeLayer') postcodeLayer: ElementRef;

  public styleClass: String;
  public layerVisible = false;
  private el: ElementRef;
  private debug = false;
  private apiReady: Promise<void>;

  constructor(
    el: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    this.el = el;
  }

  ngOnInit() {
    this.debug = this.options && this.options.debug ? this.options.debug : false;
    this.styleClass = this.options && this.options.class ? Array.isArray(this.options.class) ? this.options.class.join(' ') : this.options.class : '';
    this.apiReady = this.loadDaumApi().then(() => {
       this.print('Daum api has been loaded.');
    });
  }

  private print(msg){
    if(this.debug){
      console.log(`[${Math.floor(new Date().getTime()/1000)}]`, msg);
    }
  }

  private daumApiCallback(data){
    this.print(data);
    let fullAddr = '', extraAddr = '', engAddr = '', zipCode = '';
    if (data.userSelectedType === 'R') {
      fullAddr = data.roadAddress;
      zipCode = data.zonecode;
      engAddr = data.roadAddressEnglish;
      if(data.bname !== ''){
        extraAddr += data.bname;
      }
      if(data.buildingName !== ''){
        extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName);
      }
      fullAddr += (extraAddr !== '' ? ' ('+ extraAddr +')' : '');
    } else {
      fullAddr = data.jibunAddress;
      zipCode = data.zonecode || data.postcode;
      engAddr = data.jibunAddressEnglish;
    }

    this.closeLayer();
    this.result.emit({
      zip: zipCode,
      addr: fullAddr,
      addrEng: engAddr
    });
  }

  closeLayer() {
    this.layerVisible = false;
  }

  openDaumApi(){
    let self = this;
    const ready = this.apiReady || this.loadDaumApi();

    ready.then(() => {
      if (!window['daum'] || typeof daum.Postcode !== 'function') {
        console.error('daum.Postcode 로딩 실패');
        return;
      }

      const type = this.options && this.options.type ? this.options.type : 'popup';

      // Mac 등에서 window.open 팝업이 새 탭으로 열리는 문제 방지:
      // 기본(popup)은 페이지 내 레이어 팝업(embed)으로 표시
      if (!this.options || type === 'popup' || type === 'layer') {
        if (this.options && this.options.target) {
          const $target = this.el.nativeElement.parentElement.querySelector(`#${this.options.target}`);
          if ($target) {
            this.openExternalLayer($target, self);
            return;
          }
        }
        this.openLayerPopup(self);
        return;
      }

      if(!this.options.target){
        this.print('ERROR: Parent Component does not have a target element.');
        return false;
      }

      const $target = this.el.nativeElement.parentElement.querySelector(`#${this.options.target}`);
      this.print($target);
      if (type === 'inline') {
        let currentScroll = Math.max(document.body.scrollTop, document.documentElement.scrollTop);
        this.embedPostcode($target, {
          oncomplete: (data)=> {
            self.daumApiCallback(data);
            document.body.scrollTop = currentScroll;
          },
          onclose: ()=> $target.style.display = 'none',
          onresize : (size)=> $target.style.height = size.height+'px',
          width: '100%',
          height: '100%'
        });
        $target.style.display = 'block';
        try{
          $target.querySelector('#btnFoldWrap').onclick = ()=>{
            $target.style.display = 'none';
          };
        }catch(e){
          this.print(`ERROR: ${e.message}`);
        }
      }
    }).catch((err) => {
      console.error('daum postcode script load failed', err);
    });
  }

  private openLayerPopup(self) {
    this.layerVisible = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const $target = this.postcodeLayer && this.postcodeLayer.nativeElement;
      if (!$target) {
        console.error('우편번호 레이어 영역을 찾을 수 없습니다.');
        return;
      }

      $target.innerHTML = '';
      this.embedPostcode($target, {
        oncomplete: (data) => self.daumApiCallback(data),
        onclose: () => self.closeLayer(),
        width: '100%',
        height: '100%'
      });
    });
  }

  private openExternalLayer($target, self) {
    const width = this.options.width || 500;
    const height = this.options.height || 560;
    const border = this.options.border || 5;
    this.embedPostcode($target, {
      oncomplete: (data)=> self.daumApiCallback(data),
      onclose: ()=> $target.style.display = 'none',
      width: '100%',
      height: '100%'
    });
    $target.style.display = 'block';
    $target.style.width = `${width}px`;
    $target.style.height = `${height}px`;
    $target.style.border = `${border}px solid`;
    $target.style.left = `${(((window.innerWidth || document.documentElement.clientWidth) - width)/2 - border)}px`;
    $target.style.top = `${(((window.innerHeight || document.documentElement.clientHeight) - height)/2 - border)}px`;
    try{
      $target.querySelector('#btnCloseLayer').onclick = ()=>{
        $target.style.display = 'none';
      };
    }catch(e){
      this.print(`ERROR: ${e.message}`);
    }
  }

  private embedPostcode($target, options) {
    const embed = () => {
      new daum.Postcode(options).embed($target);
    };

    if (daum.postcode && typeof daum.postcode.load === 'function') {
      daum.postcode.load(embed);
    } else {
      embed();
    }
  }

  private loadDaumApi(){
    return new Promise<void>((resolve, reject)=> {
      if (window['daum'] && typeof daum.Postcode === 'function') {
        resolve();
        return;
      }

      const existing = document.querySelector(`script[src="${url}"]`) as HTMLScriptElement;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('daum postcode script load error')));
        if (window['daum'] && typeof daum.Postcode === 'function') {
          resolve();
        }
        return;
      }

      let script = document.createElement('script');
      script.src = url;
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('daum postcode script load error'));
      document.head.appendChild(script);
    });
  }

}
